import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Plus, LayoutGrid, List, Loader2, Eye, Filter, Edit, Trash2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Deal } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";
import { DealFormModal } from "@/components/deal-form-modal";
import { useUrlState } from "@/hooks/use-url-state";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Q9 Transaction Lifecycle Stages
type TransactionStageFilter = "all" | "scenario" | "deal" | "funded_loan";

// Scenario Substages (5 exploration phases)
const SCENARIO_SUBSTAGES = [
  { value: "initial_inquiry", label: "Initial Inquiry", color: "bg-slate-500", textColor: "text-slate-600" },
  { value: "gathering_info", label: "Gathering Info", color: "bg-blue-500", textColor: "text-blue-600" },
  { value: "seeking_indicatives", label: "Seeking Indicatives", color: "bg-indigo-500", textColor: "text-indigo-600" },
  { value: "preparing_proposal", label: "Preparing Proposal", color: "bg-purple-500", textColor: "text-purple-600" },
  { value: "awaiting_decision", label: "Awaiting Decision", color: "bg-violet-500", textColor: "text-violet-600" },
];

// Deal Substages (7 application phases)
const DEAL_SUBSTAGES = [
  { value: "application_prep", label: "Application Prep", color: "bg-amber-500", textColor: "text-amber-600" },
  { value: "submitted", label: "Submitted", color: "bg-yellow-500", textColor: "text-yellow-600" },
  { value: "lender_assessment", label: "Lender Assessment", color: "bg-orange-500", textColor: "text-orange-600" },
  { value: "conditional_approval", label: "Conditional Approval", color: "bg-lime-500", textColor: "text-lime-600" },
  { value: "formal_approval", label: "Formal Approval", color: "bg-green-500", textColor: "text-green-600" },
  { value: "settlement_booked", label: "Settlement Booked", color: "bg-cyan-500", textColor: "text-cyan-600" },
  { value: "settled", label: "Settled", color: "bg-emerald-500", textColor: "text-emerald-600" },
];

// Transaction Stage Labels
const TRANSACTION_STAGES = [
  { value: "scenario", label: "Scenario", color: "bg-blue-500", textColor: "text-blue-600" },
  { value: "deal", label: "Deal", color: "bg-purple-500", textColor: "text-purple-600" },
  { value: "funded_loan", label: "Funded Loan", color: "bg-emerald-500", textColor: "text-emerald-600" },
];

export default function DealList() {
  // Use URL state for filter persistence
  const [view, setView] = useUrlState<"kanban" | "list">("view", "kanban");
  const [transactionStageFilter, setTransactionStageFilter] = useUrlState<TransactionStageFilter>("stage", "scenario");

  // Local state (not persisted in URL)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState<Deal | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dealToDelete, setDealToDelete] = useState<Deal | null>(null);
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch deals with filter (uses default queryFn with auth headers)
  const { data: deals = [], isLoading, isError, error } = useQuery<Deal[]>({
    queryKey: ["/api/crm/deals", {
      ...(transactionStageFilter !== "all" ? { transactionStage: transactionStageFilter } : {}),
    }],
  });

  const handleAddDeal = () => {
    setEditingDeal(null);
    setIsModalOpen(true);
  };

  const handleEdit = (deal: Deal) => {
    setEditingDeal(deal);
    setIsModalOpen(true);
  };

  const handleDelete = (deal: Deal) => {
    setDealToDelete(deal);
    setDeleteDialogOpen(true);
  };

  const handleViewDeal = (dealId: string) => {
    navigate(`/deals/${dealId}`);
  };

  const confirmDelete = async () => {
    if (!dealToDelete) return;

    try {
      await apiRequest('DELETE', `/api/crm/deals/${dealToDelete.id}`);

      toast({
        title: "Deal deleted",
        description: `${dealToDelete.dealName} has been deleted successfully.`,
      });

      queryClient.invalidateQueries({ queryKey: ["/api/crm/deals"] });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete deal",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setDealToDelete(null);
    }
  };

  const handleModalClose = (open: boolean) => {
    setIsModalOpen(open);
    if (!open) {
      setEditingDeal(null);
    }
  };

  // Get columns based on current filter
  const getColumns = () => {
    if (transactionStageFilter === "scenario") {
      return SCENARIO_SUBSTAGES;
    } else if (transactionStageFilter === "deal") {
      return DEAL_SUBSTAGES;
    } else if (transactionStageFilter === "funded_loan") {
      return [{ value: "funded_loan", label: "Funded Loans", color: "bg-emerald-500", textColor: "text-emerald-600" }];
    } else {
      // "all" - show transaction stages
      return TRANSACTION_STAGES;
    }
  };

  // Group deals by column
  const dealsByColumn = () => {
    const columns = getColumns();
    const grouped = columns.reduce((acc, column) => {
      acc[column.value] = [];
      return acc;
    }, {} as Record<string, Deal[]>);

    deals.forEach((deal) => {
      if (transactionStageFilter === "scenario") {
        // Group by scenario substage
        if (deal.scenarioSubstage && grouped[deal.scenarioSubstage]) {
          grouped[deal.scenarioSubstage].push(deal);
        }
      } else if (transactionStageFilter === "deal") {
        // Group by deal substage
        if (deal.dealSubstage && grouped[deal.dealSubstage]) {
          grouped[deal.dealSubstage].push(deal);
        }
      } else if (transactionStageFilter === "funded_loan") {
        // All funded loans go in one column
        if (deal.transactionStage === "funded_loan") {
          grouped["funded_loan"].push(deal);
        }
      } else {
        // "all" - group by transaction stage
        if (deal.transactionStage && grouped[deal.transactionStage]) {
          grouped[deal.transactionStage].push(deal);
        }
      }
    });

    return grouped;
  };

  const columns = getColumns();
  const groupedDeals = dealsByColumn();

  return (
    <div className="flex h-screen w-full flex-col">
      <PageHeader
        title="Deal Pipeline"
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Deals" }]}
        actions={
          <div className="flex gap-2">
            <div className="flex rounded-md border">
              <Button
                variant={view === "kanban" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setView("kanban")}
                className="rounded-r-none"
                data-testid="button-view-kanban"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant={view === "list" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setView("list")}
                className="rounded-l-none"
                data-testid="button-view-list"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
            <Button onClick={handleAddDeal} data-testid="button-add-deal">
              <Plus className="mr-2 h-4 w-4" />
              Add Deal
            </Button>
          </div>
        }
      />

      {/* Transaction Stage Filter */}
      <div className="border-b bg-background px-4 py-3">
        <div className="flex items-center gap-3">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">View:</span>
          <Select
            value={transactionStageFilter}
            onValueChange={(value) => setTransactionStageFilter(value as TransactionStageFilter)}
          >
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="scenario">Scenarios (Exploration)</SelectItem>
              <SelectItem value="deal">Deals (Application)</SelectItem>
              <SelectItem value="funded_loan">Funded Loans</SelectItem>
              <SelectItem value="all">All Stages</SelectItem>
            </SelectContent>
          </Select>
          <div className="ml-auto text-sm text-muted-foreground">
            {deals.length} deal{deals.length !== 1 ? "s" : ""}
          </div>
        </div>
      </div>

      <main className="flex-1 overflow-auto bg-muted/20 p-4">
        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <Card className="p-8 text-center">
            <p className="text-destructive font-medium">Failed to load deals</p>
            <p className="text-sm text-muted-foreground mt-1">
              {error instanceof Error ? error.message : "An error occurred"}
            </p>
          </Card>
        ) : view === "kanban" ? (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {columns.map((column) => {
              const columnDeals = groupedDeals[column.value] || [];
              const totalValue = columnDeals.reduce((sum, deal) => sum + (Number(deal.amountRequested) || 0), 0);

              return (
                <div key={column.value} className="flex-shrink-0 w-80" data-testid={`column-${column.value}`}>
                  <Card className="h-full">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between gap-2">
                        <CardTitle className="text-xs font-semibold flex items-center gap-2">
                          <div className={`h-2.5 w-2.5 rounded-full ${column.color}`} />
                          {column.label}
                        </CardTitle>
                        <Badge variant="secondary" className="text-xs">{columnDeals.length}</Badge>
                      </div>
                      {totalValue > 0 && (
                        <div className="text-xs text-muted-foreground">
                          ${totalValue.toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </div>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {columnDeals.map((deal) => (
                        <Card
                          key={deal.id}
                          className="p-3 hover-elevate cursor-pointer group"
                          data-testid={`card-deal-${deal.id}`}
                          onClick={() => handleViewDeal(deal.id)}
                        >
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h4 className="font-semibold text-sm leading-tight">{deal.dealName}</h4>
                            <Eye className="h-3.5 w-3.5 text-muted-foreground invisible group-hover:visible shrink-0" />
                          </div>

                          {/* Transaction Stage & Substage Badges */}
                          <div className="flex flex-wrap gap-1 mb-2">
                            {deal.transactionStage && (
                              <Badge
                                variant="secondary"
                                className={`text-xs ${
                                  TRANSACTION_STAGES.find((s) => s.value === deal.transactionStage)?.color || "bg-gray-500"
                                } text-white`}
                              >
                                {TRANSACTION_STAGES.find((s) => s.value === deal.transactionStage)?.label ||
                                  deal.transactionStage}
                              </Badge>
                            )}
                            {deal.scenarioSubstage && deal.transactionStage === "scenario" && (
                              <Badge variant="outline" className="text-xs">
                                {SCENARIO_SUBSTAGES.find((s) => s.value === deal.scenarioSubstage)?.label ||
                                  deal.scenarioSubstage}
                              </Badge>
                            )}
                            {deal.dealSubstage && deal.transactionStage === "deal" && (
                              <Badge variant="outline" className="text-xs">
                                {DEAL_SUBSTAGES.find((s) => s.value === deal.dealSubstage)?.label || deal.dealSubstage}
                              </Badge>
                            )}
                          </div>

                          <div className="text-lg font-bold mb-2" data-testid="deal-amount">
                            ${Number(deal.amountRequested || 0).toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </div>
                          {deal.lenderName && (
                            <div className="text-xs text-muted-foreground mb-1">Lender: {deal.lenderName}</div>
                          )}
                          {deal.expectedSettlementDate && (
                            <div className="text-xs text-muted-foreground">
                              Expected: {format(new Date(deal.expectedSettlementDate), "dd/MM/yyyy")}
                            </div>
                          )}
                        </Card>
                      ))}
                      {columnDeals.length === 0 && (
                        <div className="text-center py-8 text-sm text-muted-foreground">
                          No deals in this stage
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mx-auto max-w-7xl">
            <Card>
              {deals.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-center">
                  <p className="text-muted-foreground">No deals found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="h-10 bg-muted/50">
                      <TableHead className="px-2 py-1 text-xs font-medium">Deal Name</TableHead>
                      <TableHead className="px-2 py-1 text-xs font-medium">Transaction Stage</TableHead>
                      <TableHead className="px-2 py-1 text-xs font-medium">Substage</TableHead>
                      <TableHead className="px-2 py-1 text-xs font-medium">Deal Type</TableHead>
                      <TableHead className="px-2 py-1 text-xs font-medium">Amount Requested</TableHead>
                      <TableHead className="px-2 py-1 text-xs font-medium">Lender</TableHead>
                      <TableHead className="px-2 py-1 text-xs font-medium">Expected Settlement</TableHead>
                      <TableHead className="px-2 py-1 text-xs font-medium text-right w-[140px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deals.map((deal) => {
                      const transactionStageConfig = TRANSACTION_STAGES.find((s) => s.value === deal.transactionStage);
                      const substageLabel =
                        deal.transactionStage === "scenario"
                          ? SCENARIO_SUBSTAGES.find((s) => s.value === deal.scenarioSubstage)?.label
                          : deal.transactionStage === "deal"
                          ? DEAL_SUBSTAGES.find((s) => s.value === deal.dealSubstage)?.label
                          : "-";

                      return (
                        <TableRow
                          key={deal.id}
                          className="h-10 hover:bg-muted/50"
                          data-testid={`row-deal-${deal.id}`}
                        >
                          <TableCell className="px-2 py-1 text-sm font-medium">{deal.dealName}</TableCell>
                          <TableCell className="px-2 py-1">
                            <Badge
                              variant="secondary"
                              className={`text-xs ${transactionStageConfig?.color || "bg-gray-500"} text-white`}
                            >
                              {transactionStageConfig?.label || deal.transactionStage || "-"}
                            </Badge>
                          </TableCell>
                          <TableCell className="px-2 py-1 text-sm text-muted-foreground">
                            {substageLabel || "-"}
                          </TableCell>
                          <TableCell className="px-2 py-1 text-sm text-muted-foreground capitalize">
                            {deal.dealType?.replace("_", " ") || "-"}
                          </TableCell>
                          <TableCell className="px-2 py-1 text-sm font-medium">
                            $
                            {Number(deal.amountRequested || 0).toLocaleString("en-AU", {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}
                          </TableCell>
                          <TableCell className="px-2 py-1 text-sm text-muted-foreground">
                            {deal.lenderName || "-"}
                          </TableCell>
                          <TableCell className="px-2 py-1 text-sm text-muted-foreground">
                            {deal.expectedSettlementDate
                              ? format(new Date(deal.expectedSettlementDate), "dd/MM/yyyy")
                              : "-"}
                          </TableCell>
                          <TableCell className="px-2 py-1">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(deal)}
                                data-testid={`button-edit-deal-${deal.id}`}
                                title="Edit deal"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(deal)}
                                data-testid={`button-delete-deal-${deal.id}`}
                                className="text-destructive hover:text-destructive"
                                title="Delete deal"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewDeal(deal.id)}
                                data-testid={`button-view-deal-${deal.id}`}
                                title="View details"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </Card>
          </div>
        )}
      </main>

      <DealFormModal open={isModalOpen} onOpenChange={handleModalClose} deal={editingDeal ?? undefined} />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Deal</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{dealToDelete?.dealName}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
